import { usePageSeo } from "@/hooks/usePageSeo";
import Navbar from "@/components/Navbar";
import StatsSection from "@/components/StatsSection";
import FeaturesSection from "@/components/FeaturesSection";
import CTASection from "@/components/CTASection";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Target, Heart, Eye, Zap, Users, Award, Clock, Globe, Shield, Palmtree } from "lucide-react";

const values = [
  { icon: Target, title: "Reliability", description: "We never miss a pickup. Track your driver in real-time and travel with confidence across the island." },
  { icon: Heart, title: "Hospitality", description: "Warm Dominican hospitality from booking to drop-off. Every guest is treated like family." },
  { icon: Eye, title: "Transparency", description: "Fixed prices, no hidden fees. What you see is what you pay — always, everywhere." },
  { icon: Zap, title: "Innovation", description: "Smart booking technology, real-time tracking, and instant confirmations at your fingertips." },
];

const AboutPage = () => {
  usePageSeo("about");
  return (
    <main className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://media.viajando.travel/p/ff85334a07385327281e625ba104b1c8/adjuntos/236/imagenes/000/742/0000742421/republica-dominicana-isla-saona-ocean-tours-rd-2jpg.jpg')` }} />
        <div className="absolute inset-0 bg-[hsl(var(--hero-bg))]/80" />
        <Navbar />
        <div className="container mx-auto px-4 relative z-10 h-full flex items-center pt-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-secondary/20 border border-secondary/30 px-4 py-2 rounded-none mb-6">
              <Globe className="w-4 h-4 text-secondary" />
              <span className="text-hero-foreground text-sm font-sans font-medium uppercase tracking-wider">About Us</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-extrabold text-hero-foreground mb-6 leading-tight">
              Your Caribbean <span className="text-secondary">Travel Partner</span>
            </h1>
            <p className="text-hero-foreground/80 font-sans text-xl leading-relaxed max-w-2xl">
              Premium tours and transportation across the Dominican Republic — delivering exceptional Caribbean experiences since day one.
            </p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-secondary" />
      </section>

      {/* Stats Strip */}
      <section className="bg-primary py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { icon: Users, value: "5000+", label: "Happy Travelers" },
              { icon: Award, value: "5-Star", label: "Service Rating" },
              { icon: Clock, value: "24/7", label: "Available" },
            ].map((stat) => (
              <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="text-center">
                <stat.icon className="w-6 h-6 text-secondary mx-auto mb-2" />
                <div className="font-display font-bold text-primary-foreground text-2xl">{stat.value}</div>
                <div className="text-primary-foreground/60 text-xs font-sans uppercase tracking-wider">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="inline-flex items-center gap-2 text-secondary font-sans text-sm font-bold uppercase tracking-widest mb-4">
                <Palmtree className="w-4 h-4" />
                Our Story
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">Born From a Love for the Dominican Republic</h2>
              <div className="space-y-4 text-muted-foreground font-sans leading-relaxed">
                <p>World Transfers Punta Cana was born from a passion for the Dominican Republic and a desire to share its beauty with the world. We saw travelers struggling with unreliable transportation and knew we could offer something better.</p>
                <p>Today, we operate a fleet of premium vehicles and offer curated tours across the island's most stunning destinations. From pristine beaches to historic cities, we make every journey unforgettable.</p>
                <p>Whether you're here for vacation, a group getaway, or adventure tours, World Transfers Punta Cana is your trusted Caribbean travel partner.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="relative">
                <img
                  src="https://media.viajando.travel/p/ff85334a07385327281e625ba104b1c8/adjuntos/236/imagenes/000/742/0000742421/republica-dominicana-isla-saona-ocean-tours-rd-2jpg.jpg"
                  alt="Dominican Republic coastline"
                  className="w-full h-96 object-cover rounded-none"
                  loading="lazy"
                />
                <div className="absolute -bottom-4 -left-4 bg-secondary px-6 py-3">
                  <p className="font-display font-bold text-primary-foreground text-sm">EST. 2024</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center">
            <Shield className="w-10 h-10 text-secondary mx-auto mb-6" />
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-foreground mb-6">Our Mission</h2>
            <p className="text-primary-foreground/80 text-lg leading-relaxed font-sans">
              To be the most trusted tour and transfer service in the Dominican Republic, delivering exceptional Caribbean experiences through professionalism, safety, and genuine hospitality.
            </p>
            <div className="w-16 h-1 bg-secondary mx-auto mt-8" />
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <p className="text-secondary font-sans font-bold text-sm uppercase tracking-widest mb-2">What Drives Us</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground">Our Core Values</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {values.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="text-center p-6 bg-card rounded-none border border-border hover:border-secondary/40 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 rounded-none bg-primary flex items-center justify-center mx-auto mb-4">
                  <v.icon className="w-6 h-6 text-primary-foreground" />
                </div>
                <h3 className="font-display text-base font-bold text-foreground mb-2">{v.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed font-sans">{v.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FeaturesSection />
      <CTASection />
      <Footer />
    </main>
  );
};

export default AboutPage;
